import React from "react";
import {
  Card,
  CardHeader,
  Body1,
  Link,
  Button,
  makeStyles,
  makeStaticStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger,
} from "@fluentui/react-components";
import { ConvertRangeRegular, Info20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import globalStyles from "./CommonStyle";

// Define custom styles using makeStyles
const useStyles = makeStyles({
  root: {
    borderRadius: "0em !important",
    boxShadow: "none",
    paddingTop: "0px",
    marginBottom: "12px",
    paddingBottom: "0px",
  },

  titleHeader: {
    fontSize: "14px",
    fontWeight: "normal !important",
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "1.5",
    letterSpacing: "normal",
  },
});

const ConversionCard: React.FC = () => {
  const globalStyle = globalStyles();
  const { t } = useTranslation();
  const classes = useStyles();

  const DR_FUNCTION_NAMES = [
    "DRAccountName",
    "DRAccountDCCode",
    "DRUnitName",
    "DRCurrencyRate",
    "DRUnitCurrency",
    "DRDatatypeName",
    "DRData",
    "DREXTRADATA",
  ];

  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false); // State for tooltip visibility
  const buttonRef = React.useRef<HTMLButtonElement>(null); // Ref for the button

  // Handle clicks outside the button to close the tooltip
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    if (isTooltipVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTooltipVisible]);

  const modernizeFunctionsWorkbook = async () => {
    try {
      await Excel.run(async (context) => {
        const sheetsCollection = context.workbook.worksheets;
        sheetsCollection.load(["items"]);
        await context.sync();

        const usedRanges = sheetsCollection.items.map((sheet) => sheet.getUsedRangeOrNullObject());
        usedRanges.forEach((range) => range.load("isNullObject"));
        await context.sync();

        for (const usedRange of usedRanges) {
          if (usedRange.isNullObject) continue;

          for (const funcName of DR_FUNCTION_NAMES) {
            const funcNameUpper = funcName.toUpperCase();
            // Strip any existing CLAUSION. prefix first to avoid double-prefixing
            usedRange.replaceAll(`CLAUSION.${funcNameUpper}(`, `${funcNameUpper}(`, {
              completeMatch: false,
              matchCase: false,
            });
            // Add CLAUSION. namespace prefix
            usedRange.replaceAll(`${funcNameUpper}(`, `CLAUSION.${funcNameUpper}(`, {
              completeMatch: false,
              matchCase: false,
            });
          }
        }

        await context.sync();
      });
    } catch (error) {
      console.error("Modernize error: ", error);
    }
  };

  return (
    <Card className={classes.root}>
      <CardHeader
        header={
          <Body1 className={globalStyle.headerContainer}>
            {/* Group the icon and title together */}
            <span className={globalStyle.titleContainer}>
              <ConvertRangeRegular />
              <span className={`${globalStyle.fontStyle}, ${globalStyle.font14}`}>{t("conversion_title")}</span>
            </span>
            <Popover>
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  ref={buttonRef} // Attach ref to the button
                  appearance="transparent"
                  icon={<Info20Regular />}
                  size="small" // Reduce button size
                  onClick={() => setIsTooltipVisible(!isTooltipVisible)} // Toggle tooltip visibility
                />
              </PopoverTrigger>

              <PopoverSurface className={globalStyle.popoverSurface}>
                <div>
                  <p>
                    {t("conversion_text")}{" "}
                  </p>
                </div>
              </PopoverSurface>
            </Popover>
          </Body1>
        }
      />
      <Button className={globalStyle.font14} onClick={() => modernizeFunctionsWorkbook()}>
        {t("conversion_migrate_button")}
      </Button>
    </Card>
  );
};

export default ConversionCard;
